// src/config/routes/public.routes.ts
import {
  ActivitySquare,
  BadgeCheck,
  BarChart3,
  Building2,
  ClipboardList,
  Cog,
  FileBarChart2,
  FileSearch,
  FileText,
  Flame,
  Gauge,
  HeadphonesIcon,
  LayoutDashboard,
  Leaf,
  ListChecks,
  ScrollText,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Siren,
  TrendingDown,
  Users,
  Wind,
  Zap,
  Cpu,
  Battery,
  Sun,
  Network,
  GitCompareArrows,
} from "lucide-react";
import { lazy } from "react";
import { RouteConfig } from "src/types/route-config.types";

export const protectedRoutes: RouteConfig[] = [
  {
    name: "Dashboard",
    path: "/dashboard",
    component: lazy(() => import("src/pages/dashboard/DashboardMain")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard"],
    icon: <LayoutDashboard className="h-4 w-4" />, // 🏠 Dashboard
    order: 2.1,
    meta: { title: "", description: "" },
  },

  {
    name: "Renewable Energy Monitoring",
    path: "/renewable-energy-monitoring",
    component: lazy(
      () =>
        import("src/components/dashboard/RenuableEnergyChart/EnergyDashboard"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["dashboard"],
    icon: <Leaf className="h-4 w-4" />, // 🌿 Renewable / Green Energy
    order: 3,
    meta: { title: "", description: "" },
  },

  {
    name: "DUCK",
    path: "/duck-curv",
    component: lazy(() => import("src/components/dashboard/DuckCurvePage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["dashboard"],
    icon: <Leaf className="h-4 w-4" />, // 🌿 Renewable / Green Energy
    order: 3,
    meta: { title: "", description: "" },
  },

  {
    name: "Energy Monitoring",
    path: "/energy-monitoring",
    component: lazy(
      () => import("src/pages/energy-monitoring/EnergyMonitoringDashboardPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["dashboard"],
    icon: <Gauge className="h-4 w-4" />, // ⚡ Energy Monitor
    order: 3,
    meta: {
      title: "Energy Monitoring Dashboard",
      description:
        "Monitor energy generation and consumption with detailed analytics.",
    },
  },

  {
    name: "Energy Loss Monitoring",
    path: "/energy-loss-monitoring",
    component: lazy(
      () =>
        import("src/pages/energy-monitoring/energy-loss/EnergyLossMOnitoringDashboardMain"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["dashboard"],
    icon: <TrendingDown className="h-4 w-4" />, // 📉 Energy Loss
    order: 3,
  },

  // --- energy audit ---
  {
    name: "Consumer Energy Audit Details",
    path: "/energy-audit/consumer-details",
    component: lazy(
      () =>
        import("src/pages/audit-report/consumer/audit-details/ConsumerAuditDetails"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["dashboard"],
    icon: <FileSearch className="h-4 w-4" />, // 🔍 Audit Details
    order: 3,
    meta: {
      title: "Consumer Energy Audit",
      description: "Monitor energy consumption with detailed analytics.",
    },
  },
  {
    name: "Generator Energy Audit",
    path: "/energy-audit/generator-details",
    component: lazy(
      () =>
        import("src/pages/audit-report/generator/audit-details/GeneratorAuditDetails"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["dashboard"],
    icon: <FileSearch className="h-4 w-4" />, // 🔍 Audit Details
    order: 3,
    meta: {
      title: "Generator Energy Audit",
      description:
        "Monitor energy generation and consumption with detailed analytics.",
    },
  },
  {
    name: "Generation Energy Audit Report",
    path: "/energy-audit/generator-report",
    component: lazy(
      () =>
        import("src/pages/audit-report/generator/audit-report/GeneratorAuditReport"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["energy-setting", "Energy Setting Form"],
    icon: <FileBarChart2 className="h-4 w-4" />, // 📊 Audit Report
    order: 8.2,
    meta: {
      title: "Generator Energy Audit Report",
      description: "Generation energy audit report with detailed analytics",
    },
  },
  {
    name: "Consumer Energy Audit Report",
    path: "/energy-audit/consumer-report",
    component: lazy(
      () =>
        import("src/pages/audit-report/consumer/audit-report/ConsumerAuditReport"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["energy-setting", "Energy Setting Form"],
    icon: <FileBarChart2 className="h-4 w-4" />, // 📊 Audit Report
    order: 8.2,
    meta: {
      title: "Consumer Energy Audit Report",
      description: "Consumption energy audit report with detailed analytics",
    },
  },

  // --- Onboarding Section ---
  {
    name: "Admin",
    path: "/onboarding/admin",
    component: lazy(
      () => import("src/pages/onboarding/admin/admin-report/AdminReportPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Onboarding",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Onboarding", "Admin"],
    icon: <ShieldCheck className="h-4 w-4" />, // 🛡️ Admin
    order: 2.1,
    meta: {
      title: "Onboard Admin User",
      description: "Register and configure new admin user",
    },
  },
  {
    name: "Generators",
    path: "/onboarding/generator",
    component: lazy(
      () =>
        import("src/pages/onboarding/generators/generators-report/GeneratorsReportPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Onboarding",
    isDefaultFor: ["admin", "administrator"],
    access: ["admin", "administrator"],
    breadcrumbPath: ["Onboarding", "generator"],
    icon: <Zap className="h-4 w-4" />, // ⚡ Generators
    order: 2.1,
    meta: {
      title: "Generator Onboarding",
      description: "Register and configure green energy generators.",
    },
  },
  {
    name: "Generator Profile",
    path: "/onboarding/generator/profile-details/:consumer_id",
    component: lazy(
      () =>
        import("src/pages/onboarding/generators/generator-profile/GeneratorProfilePage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "Onboarding",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Onboarding", "generator"],
    icon: <Zap className="h-4 w-4" />, // ⚡ Generator Profile
    order: 2.1,
  },
  {
    name: "Consumer",
    path: "/onboarding/consumer",
    component: lazy(
      () =>
        import("src/pages/onboarding/consumers/consumers-report/ConsumersReportPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Onboarding",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Onboarding", "consumer"],
    icon: <Users className="h-4 w-4" />, // 👥 Consumers
    order: 2.1,
    meta: {
      title: "Consumer Onboarding",
      description: "Register and configure green energy consumers.",
    },
  },

  {
    name: "Consumers Profile",
    path: "/onboarding/consumer/profile-details/:consumer_id",
    component: lazy(
      () =>
        import("src/pages/onboarding/consumers/consumer-profile/ConsumerProfilePage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "Onboarding",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Onboarding", "consumer"],
    icon: <Users className="h-4 w-4" />, // 👥 Consumer Profile
    order: 2.1,
    meta: {
      title: "Onboard consumer",
      description: "Energy generators details",
    },
  },

  // --- GEC Certificate Management Section ---
  {
    name: "GEC List",
    path: "/gec-management/issuance",
    component: lazy(
      () => import("src/pages/gec/gec-list-Issuance-report/DashboardPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["GEC Certificate Management", "GEC List / Issuance"],
    icon: <BadgeCheck className="h-4 w-4" />, // ✅ GEC Certificate
    order: 8.1,
    meta: {
      title: "Green Energy Certificate",
      description: "List and issuance details of Green Energy Certificates.",
    },
  },
  {
    name: "GEC Tracibility Report",
    path: "/gec-management/tracibility-report/:id",
    component: lazy(
      () =>
        import("src/pages/gec/gec-tracibility-report/GRCTracibilityReportPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "GEC Certificate Management",
    access: ["admin"],
    breadcrumbPath: ["GEC Certificate Management", "GEC Tracibility"],
    icon: <ListChecks className="h-4 w-4" />, // ✔️ Traceability
    order: 8.1,
    meta: {
      title: "GEC List & Issuance",
      description: "To register an eligible consumer for energy usage.",
    },
  },

  // --- Configuration Section ---
  {
    name: "Time Block Configuration",
    path: "/configuration/configuration-form",
    component: lazy(
      () => import("src/pages/configuration/time-block/ConfigurationPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Configuration",
    access: ["admin"],
    breadcrumbPath: ["Configuration", "Configuration Form"],
    icon: <SlidersHorizontal className="h-4 w-4" />, // 🎚️ Time Block / Config
    order: 8.2,
    meta: { title: "Time Block", description: "" },
  },
  {
    name: "Other Configuration",
    path: "/configuration/energy-setting",
    component: lazy(
      () => import("src/pages/configuration/energy-setting/ConfigurationPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Configuration",
    access: ["admin"],
    breadcrumbPath: ["energy-setting", "Energy Setting Form"],
    icon: <Settings2 className="h-4 w-4" />, // ⚙️ Energy Settings
    order: 8.2,
    meta: { title: "Energy Setting", description: "" },
  },

  // --- Self Service ---
  {
    name: "Energy Dashboard",
    path: "/energy-dashboard",
    component: lazy(
      () =>
        import("src/pages/energy-summary-dashboard/EnergySummaryDashboardMainPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["Energy Dashboard"],
    icon: <BarChart3 className="h-4 w-4" />, // 📊 Energy Summary
    order: 8.2,
  },
  {
    name: "Company List",
    path: "/company-list",
    component: lazy(
      () =>
        import("src/pages/comapny-consumers-report/company-report/CompanyReportMainPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["Company List"],
    icon: <Building2 className="h-4 w-4" />, // 🏢 Company
    order: 8.2,
    meta: { title: "Company List", description: "" },
  },
  {
    name: "Consuners List",
    path: "/cunsumers-list/:consumer_id",
    component: lazy(
      () =>
        import("src/pages/comapny-consumers-report/consumers-report/ConsumerReportMainPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["Company List"],
    icon: <Users className="h-4 w-4" />, // 👥 Consumers List
    order: 8.2,
    meta: { title: "Company List", description: "" },
  },
  {
    name: "Help & Support",
    path: "/self-servivce/query-requests",
    component: lazy(
      () => import("src/pages/self-service/query-requests/QueryRequestsPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin"],
    breadcrumbPath: ["Self-Service", "Query Requests"],
    icon: <HeadphonesIcon className="h-4 w-4" />, // 🎧 Help & Support
    order: 9,
    meta: {
      title: "Help & Support",
      description: "Get assistance with your green energy queries.",
    },
  },

  // --- Reports Section ---
  {
    name: "BEE-RCO-Form",
    path: "/bee-rco-report/form",
    component: lazy(
      () => import("src/pages/bee/rco-report/RcoReportFormMainPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "Reports",
    access: ["admin"],
    breadcrumbPath: ["Opt-In"],
    icon: <ClipboardList className="h-4 w-4" />, // 📋 Form / BEE
    order: 1,
  },
  {
    name: "BEE-RCO-Report",
    path: "/bee-rco-report/report",
    component: lazy(() => import("src/pages/bee/rco-report/RcoReportMainPage")),
    showInSideNav: true,
    sideNavGroupName: "Reports",
    access: ["admin"],
    breadcrumbPath: ["Opt-In"],
    icon: <FileText className="h-4 w-4" />, // 📄 Report
    order: 1,
  },

  // --- Basic App Routes (Added) ---

  // import { lazy } from "react";
  // import { LayoutDashboard, Gauge, BarChart3, Siren } from "lucide-react";

  // --- RTS Impact Dashboard ---
  // --- RTS Impact ---
  {
    name: "RTS Impact",
    path: "/rts-impact",
    component: lazy(() => import("src/pages/rst-impact/Dashboard")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard"],
    icon: <LayoutDashboard className="h-4 w-4" />,
    order: 1,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "Hosting",
    path: "/hosting-capacity-dashbaord",
    component: lazy(
      () => import("src/pages/hosting-capacity/HostingCapacityPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard"],
    icon: <LayoutDashboard className="h-4 w-4" />,
    order: 1,
    meta: {
      title: "",
      description: "",
    },
  },

  // --- Feeder Impact ---
  {
    name: "Feeder Impact",
    path: "/feeder-impact",
    component: lazy(() => import("src/pages/rst-impact/FeederDashboard")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard", "Feeder Impact"],
    icon: <Network className="h-4 w-4" />,
    order: 1.1,
    meta: {
      title: "Feeder Impact",
      description: "Feeder-level RTS impact dashboard",
    },
  },
  {
    name: "Feeder Detail",
    path: "/feeder/:id",
    component: lazy(() => import("src/pages/rst-impact/FeederDetail")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard", "Feeder Impact", "Feeder Detail"],
    icon: <Gauge className="h-4 w-4" />,
    order: 1.2,
    meta: {
      title: "Feeder Detail",
      description: "Detailed feeder analytics",
    },
  },

  {
    name: "Heat",
    path: "/heat",
    component: lazy(
      () =>
        import("src/components/rst-impact/tabs/TransformerReverseFlowHeatmap"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard"],
    icon: <LayoutDashboard className="h-4 w-4" />,
    order: 1,
    meta: {
      title: "RTS Impact Dashboard",
      description: "RTS impact overview",
    },
  },

  {
    name: "Energy Analytics",
    path: "/energy-analytics",
    component: lazy(
      () => import("src/pages/energy-analytics/EnergyAnalyticsPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard"],
    icon: <LayoutDashboard className="h-4 w-4" />,
    order: 1,
    meta: {
      title: "RTS Impact Dashboard",
      description: "RTS impact overview",
    },
  },

  // --- Transformer Detail ---
  {
    name: "Transformer Detail",
    path: "/transformer/:id",
    component: lazy(() => import("src/pages/rst-impact/TransformerDetail")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Dashboard", "Transformer Detail"],
    icon: <Gauge className="h-4 w-4" />,
    order: 2,
    meta: {
      title: "Transformer Detail",
      description: "Detailed transformer analytics",
    },
  },

  // --- Compare View ---
  {
    name: "Compare View",
    path: "/compare",
    component: lazy(() => import("src/pages/rst-impact/CompareView")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", "Compare"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 2.1,
    meta: {
      title: "Compare Transformers",
      description: "Compare transformer performance",
    },
  },

  // --- Alerts ---
  {
    name: "Alerts",
    path: "/alerts",
    component: lazy(() =>
      import("src/pages/rst-impact/AlertsView").then((module: any) => ({
        default: module.default || module.AlertsView,
      })),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", "Alerts"],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "Alerts",
      description: "System alerts and notifications",
    },
  },

  {
    name: "Transformer Analytics",
    path: "/optimized-stack-chart",
    component: lazy(
      () => import("src/pages/transformer-analytics/transformer-analytics"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["dashboard"],
    icon: <Leaf className="h-4 w-4" />,
    order: 3,
    meta: { title: "", description: "" },
  },

  {
    name: "KPI",
    path: "/kpi-table",
    component: lazy(() => import("src/pages/rst-impact/KPITableView")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "KPI",
    path: "/kpi-table-re",
    component: lazy(() => import("src/pages/kpi-analysis/KpiAnalysisPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "OV",
    path: "/ov/:id?",
    component: lazy(() => import("src/pages/over-voltage/OverVoltagePage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "KPI New",
    path: "/kpi-table-new",
    component: lazy(() => import("src/pages/rst-impact/KPITableView")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "Feeder KPI",
    path: "/feeder-kpi-table",
    component: lazy(() => import("src/pages/feeder-kpi/FeederKPIPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Network className="h-4 w-4" />,
    order: 2.21,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "trcd-dashboard",
    path: "/trcd-dashboard",
    component: lazy(() => import("src/pages/trcd-dashboard/TrcdDashboard")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },
  {
    name: "tri-dashboard",
    path: "/tri-dashboard",
    component: lazy(() => import("src/pages/tri-dashboard/TRIDashboard")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },
  // {
  //   name: "feeder-dashboard",
  //   path: "/feeder-dashboard",
  //   component: lazy(() =>
  //     import("src/pages/feeder-impact/FeederImpactDashboard").then(
  //       (module) => ({
  //         default: module.FeederImpactDashboard, // named export se map karo
  //       }),
  //     ),
  //   ),
  //   // component: lazy(() => import("src/pages/feeder-impact/FeederImpactDashboard")),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },
  // {
  //   name: "DTR Index",
  //   path: "/dtr-index-kpi-impact",
  //   component: lazy(
  //     () => import("src/pages/dtr-index-kpi-impact/DtrIndexKpiImpactDashboardPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },
  {
    name: "Heat Map Feeder",
    path: "/heatmap",
    component: lazy(() =>
      import("src/pages/risk-matrix/RiskMatrixFeederPage").then((module) => ({
        default: module.FeederImpactDashboard, // named export se map karo
      })),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "Heat Map Feeder",
    path: "/risk-matrix/transformers",
    component: lazy(
      () => import("src/pages/risk-matrix/RiskMatrixTransformerPage"),
    ),

    // rc/pages/risk-matrix/RiskMatrixTransformerPage
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  // {
  //   name: "risk feeder chart",
  //   path: "/risk feeder chart",
  //   component: lazy(
  //     () =>
  //       import("src/pages/feeder-impact/RiskyFeedersChart/RiskyFeedersChartPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  // {
  //   name: "System TRI Trend",
  //   path: "/system-tri-trend",
  //   component: lazy(
  //     () => import("src/pages/feeder-impact/SystemTRITrend/SystemTRITrendPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  // {
  //   name: "Feeder Risk Ranking Chart",
  //   path: "/feeder-risk-ranking-chart",
  //   component: lazy(
  //     () =>
  //       import("src/pages/feeder-impact/FeederRiskRankingChart/FeederRiskRankingChartPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  // {
  //   name: "Time Series RootCause",
  //   path: "/time-series-root-cause",
  //   component: lazy(
  //     () =>
  //       import("src/pages/feeder-impact/TimeSeriesRootCauseChart/TimeSeriesRootCauseChartPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  // {
  //   name: "Phase Analysis",
  //   path: "/phase-analysis",
  //   component: lazy(
  //     () =>
  //       import("src/pages/feeder-impact/PhaseAnalysisChart/PhaseAnalysisChartPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  //  {
  //   name: "Transformer",
  //   path: "/transformer-kpi",
  //   component: lazy(
  //     () =>
  //       import("src/pages/feeder-impact/TransformerKpiCharts/TransformerKpiChartsPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  {
    name: "Transformer-Dashboard",
    path: "/transformer-dash",
    component: lazy(
      () => import("src/pages/master-dashboard/MasterDashboardPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  // {
  //   name: "Transformer-passport",
  //   path: "/transformer-passport",
  //   component: lazy(
  //     () =>
  //       import("src/features/feeder-impact/components/ReliabilityEventTimeline/ReliabilityEventTimeline"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  {
    name: "Division Performance",
    path: "/division-performance",
    component: lazy(
      () => import("src/pages/division-performance/DivisionPerformancePage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "Feeder Performance",
    path: "/Feeder-performance",
    component: lazy(
      () => import("src/pages/feeder-performance/FeederPerformancePage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "Feeder & Transformer Compare",
    path: "/feeder-transformer-compare",
    component: lazy(
      () =>
        import("src/pages/feeder-transformer-compare/FeederTransformerComparePage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", "Compare"],
    icon: <GitCompareArrows className="h-4 w-4" />,
    order: 2.25,
    meta: {
      title: "Feeder & Transformer Compare",
      description: "Compare feeders and transformers side by side",
    },
  },

  {
    name: "Grid Preformece",
    path: "/grid-performance",
    component: lazy(
      () => import("src/pages/grid-performance/GridPerformancePage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "config",
    path: "/config-var",
    component: lazy(
      () => import("src/pages/control-config/ConfigurationMetricPage"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "export",
    path: "/export-transformer",
    component: lazy(() => import("src/pages/exportTransfomer/hrTransfomer")),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "hosting",
    path: "/hosting-capacity",
    component: lazy(
      () => import("src/pages/hosting-capacity/HostingCapacityDashboard"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  {
    name: "EII",
    path: "/export-Intensity-index",
    component: lazy(
      () => import("src/pages/eii/ExportIntensityIndexDashboard"),
    ),
    showInSideNav: true,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Dashboard", ""],
    icon: <Siren className="h-4 w-4" />,
    order: 2.2,
    meta: {
      title: "",
      description: "",
    },
  },

  // {
  //   name: "EII",
  //   path: "/export-Intensity-index-new",
  //   component: lazy(
  //     () => import("src/pages/export-energy-index/ExportEnergyIndexPage"),
  //   ),
  //   showInSideNav: true,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator"],
  //   breadcrumbPath: ["Dashboard", ""],
  //   icon: <Siren className="h-4 w-4" />,
  //   order: 2.2,
  //   meta: {
  //     title: "",
  //     description: "",
  //   },
  // },

  {
    name: "Monthly Energy",
    path: "/energy-analytics/monthly",
    component: lazy(
      () => import("src/components/energy-analytics/MonthlyEnergyPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Monthly"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Monthly Energy Trend",
      description: "DT energy trend on a monthly basis",
    },
  },
  {
    name: "Daily Energy",
    path: "/energy-analytics/daily",
    component: lazy(
      () => import("src/components/energy-analytics/DailyEnergyPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Daily"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Daily Energy Trend",
      description: "DT energy trend on a daily basis",
    },
  },
  {
    name: "Hourly Energy",
    path: "/energy-analytics/hourly",
    component: lazy(
      () => import("src/components/energy-analytics/HourlyEnergyPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Hourly"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Hourly Energy Trend",
      description: "DT energy trend on an hourly basis",
    },
  },

  {
    name: "Energy Monitoring - Daily",
    path: "/energy-monitoring/daily",
    component: lazy(
      () => import("src/pages/energy-monitoring/EnergyMonitoringViewPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Energy Monitoring", "Daily"],
    icon: <Gauge className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Daily Energy Monitoring",
      description: "Daily energy generation, consumption & settlement analysis",
    },
  },
  {
    name: "Energy Monitoring - Weekly",
    path: "/energy-monitoring/weekly",
    component: lazy(
      () => import("src/pages/energy-monitoring/EnergyMonitoringViewPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Energy Monitoring", "Weekly"],
    icon: <Gauge className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Weekly Energy Monitoring",
      description:
        "Weekly energy generation, consumption & settlement analysis",
    },
  },
  {
    name: "Energy Monitoring - Monthly",
    path: "/energy-monitoring/monthly",
    component: lazy(
      () => import("src/pages/energy-monitoring/EnergyMonitoringViewPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Energy Monitoring", "Monthly"],
    icon: <Gauge className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Monthly Energy Monitoring",
      description:
        "Monthly energy generation, consumption & settlement analysis",
    },
  },
  {
    name: "Energy Monitoring - Annual",
    path: "/energy-monitoring/annual",
    component: lazy(
      () => import("src/pages/energy-monitoring/EnergyMonitoringViewPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Energy Monitoring", "Annual"],
    icon: <Gauge className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Annual Energy Monitoring",
      description:
        "Annual energy generation, consumption & settlement analysis",
    },
  },

  {
    name: "Duck Curve",
    path: "/duck-curve",
    component: lazy(() => import("src/components/dashboard/DuckCurvePage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "Duck Curve Analysis",
      description:
        "24-hour load profile with solar impact, voltage, current, and load profiles",
    },
  },

  {
    name: "EII",
    path: "/eii-dashboard",
    component: lazy(() => import("src/pages/eii/EEIDashboardPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    // meta: {
    //   title: "",
    //   description:
    //     "",
    // },
  },
  {
    name: "EII",
    path: "/hosting-capacity-dashboard",
    component: lazy(() => import("src/pages/hc-approval/HcApprovalPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    // meta: {
    //   title: "",
    //   description:
    //     "",
    // },
  },
  {
    name: "EII Details",
    path: "/eii-details/:id",
    component: lazy(() => import("src/pages/eii/EEIDetails")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "EII Dashboard", "EII Details"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 4,
  },

  // {
  //   name: "Hosting Capacity",
  //   path: "/hosting-capacity-dashboard",
  //   component: lazy(
  //     () => import("src/pages/hosting-capacity/HostingCapacityDashboardPage"),
  //   ),
  //   showInSideNav: false,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator", "consumer"],
  //   breadcrumbPath: ["Gridwatch", "Duck Curve"],
  //   icon: <TrendingDown className="h-4 w-4" />,
  //   order: 3,
  //   // meta: {
  //   //   title: "",
  //   //   description:
  //   //     "",
  //   // },
  // },

  {
    name: "Slot Price",
    path: "/net-metering",
    component: lazy(() => import("src/pages/net-metering/NetMeteringPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    // meta: {
    //   title: "",
    //   description:
    //     "",
    // },
  },

  {
    name: "Risk Index",
    path: "/risk-index",
    component: lazy(() => import("src/features/risk-register/RiskRegister")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    // meta: {
    //   title: "",
    //   description:
    //     "",
    // },
  },

  {
    name: "line prosumer",
    path: "/rts-customers-graph",
    component: lazy(
      () =>
        import("src/features/prosumer-line-chart/pages/ProsumerLineChartPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Gridwatch", "Duck Curve"],
    icon: <TrendingDown className="h-4 w-4" />,
    order: 3,
    // meta: {
    //   title: "",
    //   description:
    //     "",
    // },
  },

  {
    name: "DER Registry Raw Data",
    path: "/rts-registry/consumer-classification",
    component: lazy(
      () => import("src/features/rts-registry/components/DerRegistryRawData"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["RTS Registry", "Raw Data"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "DER Registry - Raw Data",
      description: "Raw data view of DER Registry consumers and DTs",
    },
  },

  // {
  //   name: "Superset",
  //   path: "/superset-dashboard",
  //   component: lazy(
  //     () => import("src/pages/superset/pages/SupersetDashboardPage"),
  //   ),
  //   showInSideNav: false,
  //   sideNavGroupName: "",
  //   access: ["admin", "administrator", "consumer"],
  //   breadcrumbPath: ["Gridwatch", "Duck Curve"],
  //   icon: <TrendingDown className="h-4 w-4" />,
  //   order: 3,
  //   // meta: {
  //   //   title: "",
  //   //   description:
  //   //     "",
  //   // },
  // },

  // Routes file me ye add karo

  {
    name: "Superset - Dashboard",
    path: "/superset-dashboard/dashboard",
    component: lazy(
      () => import("src/pages/superset/pages/SupersetDashboardPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "Dashboard"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
  },
  {
    name: "Superset - Banking Loss",
    path: "/superset-dashboard/banking-loss",
    component: lazy(
      () => import("src/pages/superset/pages/SupersetDashboardPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "Banking Loss"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
  },
  {
    name: "Superset - DT GEC",
    path: "/superset-dashboard/dt-gec",
    component: lazy(
      () => import("src/pages/superset/pages/SupersetDashboardPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "DT - GEC"],
    icon: <Cpu className="h-4 w-4" />,
    order: 3,
  },
  {
    name: "Superset - GEC BESS",
    path: "/superset-dashboard/gec-bess",
    component: lazy(
      () => import("src/pages/superset/pages/SupersetDashboardPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "GEC BESS"],
    icon: <Battery className="h-4 w-4" />,
    order: 3,
  },
  {
    name: "Superset - RTS",
    path: "/superset-dashboard/rts",
    component: lazy(
      () => import("src/pages/superset/pages/SupersetDashboardPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "RTS"],
    icon: <Sun className="h-4 w-4" />,
    order: 3,
  },

  {
    name: "RE-Reports",
    path: "/reports/re-assure",
    component: lazy(() => import("src/components/re-reports/ReportsPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator", "consumer"],
    breadcrumbPath: ["Superset", "RTS"],
    icon: <Sun className="h-4 w-4" />,
    order: 3,
  },

  {
    name: "RTS Summary",
    path: "/rts-summary",
    component: lazy(() => import("src/pages/rts-summary/RtsSummaryPage")),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Green Book", "RTS Summary"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "RTS Summary",
      description: "Monthly Settlement Summary",
    },
  },
  {
    name: "DT Export Index",
    path: "/dt-export-index",
    component: lazy(
      () => import("src/pages/dt-export-index/DtExportIndexPage"),
    ),
    showInSideNav: false,
    sideNavGroupName: "",
    access: ["admin", "administrator"],
    breadcrumbPath: ["Green Book", "RTS Summary"],
    icon: <BarChart3 className="h-4 w-4" />,
    order: 3,
    meta: {
      title: "",
      description: "",
    },
  },
];
